/**
 * Строка метрик под видео в отчёте «Топ видео»:
 * «685 тыс. просмотров, 66,4 тыс. лайков, 316 комментариев, 2 195 сохранений
 * и 1 814 репостов».
 *
 * Формат снят с файла, который в медиа собирали руками. Там числа записаны
 * непоследовательно (в одном ролике «2 195 сохранений», в другом «1,1 тыс.»),
 * потому что их переписывали из интерфейса. Здесь правило одно и явное:
 * просмотры и лайки — крупные числа, их сокращаем; комментарии, сохранения и
 * репосты пишем точно, там сокращение только мешает.
 */

const THOUSAND = 1000;
const MILLION = 1_000_000;

/** Русские склонения: 1 просмотр, 2 просмотра, 5 просмотров. */
export function plural(n: number, one: string, few: string, many: string): string {
  const abs = Math.abs(n) % 100;
  const last = abs % 10;
  if (abs > 10 && abs < 20) return many;
  if (last === 1) return one;
  if (last >= 2 && last <= 4) return few;
  return many;
}

/** Один знак после запятой, и тот отбрасывается, если ноль: 685, а не 685,0. */
function withUnit(n: number, divisor: number, unit: string): string {
  const value = Math.round((n / divisor) * 10) / 10;
  const text = Number.isInteger(value) ? String(value) : value.toFixed(1).replace('.', ',');
  return `${text} ${unit}`;
}

/** Крупные числа сокращаем: 66 400 → «66,4 тыс.», 3 400 000 → «3,4 млн». */
export function compactRu(n: number): string {
  if (!Number.isFinite(n)) return '0';
  if (Math.abs(n) >= MILLION) return withUnit(n, MILLION, 'млн');
  if (Math.abs(n) >= THOUSAND) return withUnit(n, THOUSAND, 'тыс.');
  return exactRu(n);
}

/** Точное число с неразрывным пробелом, чтобы Word не рвал «2 195» по строкам. */
export function exactRu(n: number): string {
  return Math.round(n).toLocaleString('ru-RU');
}

export type VideoMetrics = {
  views: number;
  likes: number;
  comments: number;
  shares: number;
  /**
   * null — площадка сохранения не отдаёт вовсе (так у TikTok: в Display API
   * такого поля нет). Это не то же самое, что ноль: «0 сохранений» в отчёте
   * читается как провал ролика, поэтому пункт просто выпадает из строки.
   */
  saved: number | null;
};

/** Число + существительное в нужном падеже. После «тыс.» и «млн» — всегда родительный. */
function part(n: number, compact: boolean, one: string, few: string, many: string): string {
  const shortened = compact && Math.abs(n) >= THOUSAND;
  const value = compact ? compactRu(n) : exactRu(n);
  return `${value} ${shortened ? many : plural(n, one, few, many)}`;
}

/** «A, B, C и D» — запятые между всеми, «и» перед последним. */
function joinRu(items: string[]): string {
  if (items.length <= 1) return items[0] ?? '';
  return `${items.slice(0, -1).join(', ')} и ${items[items.length - 1]}`;
}

export function formatMetricsLine(m: VideoMetrics): string {
  const items = [
    part(m.views, true, 'просмотр', 'просмотра', 'просмотров'),
    part(m.likes, true, 'лайк', 'лайка', 'лайков'),
    part(m.comments, false, 'комментарий', 'комментария', 'комментариев'),
    ...(m.saved === null ? [] : [part(m.saved, false, 'сохранение', 'сохранения', 'сохранений')]),
    part(m.shares, false, 'репост', 'репоста', 'репостов'),
  ];
  return `${joinRu(items)}.`;
}
